import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const steps = [
  {
    number: "01",
    title: "Sign Up as an Instructor",
    description: "Visit NeoMaestro and create your free instructor account. Just enter your name, email, password, and a short bio about your expertise.",
    details: [
      "Go to neomaestro.in and click 'Join Now'",
      "Select 'Instructor' as your role",
      "Fill in your details and area of expertise",
      "Your account is ready instantly"
    ],
    color: "#00a3af"
  },
  {
    number: "02",
    title: "Access Your Dashboard",
    description: "After signing in, you'll land on your personal Instructor Dashboard. This is your control center for managing all your masterclasses and tracking performance.",
    details: [
      "View all your listed masterclasses",
      "Track clicks and engagement analytics",
      "See approval status of each listing",
      "Manage your plan and account"
    ],
    color: "#004d7a"
  },
  {
    number: "03",
    title: "Create Your Masterclass",
    description: "Click 'Create Masterclass' and fill in the details. Our AI assistant can even help you write a compelling description!",
    details: [
      "Enter title, category, date, duration and price",
      "Add your external registration/payment link",
      "Upload a cover image for your class",
      "Use AI Generate to craft the perfect description"
    ],
    color: "#34c759"
  },
  {
    number: "04",
    title: "Admin Review & Approval",
    description: "Our team reviews your listing to ensure quality standards. Most listings are approved within 24 hours.",
    details: [
      "Listing status shows as 'Pending' during review",
      "You'll be notified once approved",
      "You can edit your listing anytime",
      "Rejected listings include feedback for improvement"
    ],
    color: "#f59e0b"
  },
  {
    number: "05",
    title: "Go Live & Get Leads",
    description: "Once approved, your masterclass appears on the marketplace. Learners discover your class, click 'Register Now', and are redirected to your registration page.",
    details: [
      "Your class is visible to thousands of learners",
      "Track every click from your dashboard",
      "Learners are sent directly to your registration link",
      "You keep 100% of your earnings — zero commission"
    ],
    color: "#00a3af"
  }
];

export default function OnboardingGuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="gradient-header py-4 px-6">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <Button
            onClick={() => navigate('/auth?role=instructor')}
            className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-6"
            data-testid="header-signup-btn"
          >
            Get Started Free
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 text-center bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto"
        >
          <span className="inline-block bg-[#00a3af]/10 text-[#00a3af] font-bold text-sm px-4 py-2 rounded-full mb-6">
            Instructor Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
            List Your Masterclass in<br />
            <span className="text-[#00a3af]">5 Simple Steps</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Join India's growing community of expert instructors. Reach thousands of learners and grow your masterclass business.
          </p>
        </motion.div>
      </section>

      {/* Steps */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-16 last:mb-0"
            >
              <div className={`flex flex-col md:flex-row items-start gap-8 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                {/* Step Number */}
                <div className="flex-shrink-0">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-black"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.number}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h2>
                  <p className="text-gray-600 mb-5 text-base leading-relaxed">{step.description}</p>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <ul className="space-y-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: step.color }}
                          >
                            {i + 1}
                          </span>
                          <span className="text-gray-700">
                            {detail.includes('neomaestro.in') 
                              ? <>Go to <a href="https://neomaestro.in" target="_blank" rel="noopener noreferrer" className="text-[#00a3af] underline font-medium">neomaestro.in</a> and click 'List your masterclass'</>
                              : detail
                            }
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="flex justify-center md:justify-start md:ml-10 my-6">
                  <div className="w-0.5 h-12 bg-gray-200 rounded-full" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Ready to Reach Thousands of Learners?</h2>
          <Button
            onClick={() => navigate('/auth?role=instructor')}
            className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-10 py-6 text-lg font-bold"
            data-testid="cta-signup-btn"
          >
            Get Started Now
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <p>&copy; 2026 NeoMaestro. All rights reserved.</p>
      </footer>
    </div>
  );
}
